import type {
  CollectionMethod,
  PlanId,
  SubscriptionStatus
} from '@tandemone/types'
import type { QueryClient } from '@tanstack/react-query'
import type { Socket } from 'socket.io-client'

import { BILLING_OVERVIEW_KEY } from './keys'

type SubscriptionChanged = {
  planId: PlanId
  planLabel: string
  status: SubscriptionStatus
  collectionMethod: CollectionMethod
  companyId: string
  /* the rep's plan changed. its suppliers refresh but get no toast, since it
   * isn't their own plan. */
  coveredByRep: boolean
}

/* a change in this tab already toasts from its own mutation, and the broadcast
 * it triggers would stack a second one. the mutation claims a window rather
 * than a single event: one action makes stripe send several webhooks, and each
 * one broadcasts, so a one-shot flag let the second through. */
let suppressedUntil = 0

export function suppressNextSubscriptionToast(): void {
  suppressedUntil = Date.now() + 15_000
}

/* entitlements ride setup state, so both caches have to drop or the page keeps
 * rendering the old plan. */
export function installBillingSocketHandlers(
  socket: Socket,
  queryClient: QueryClient,
  onActivated: (payload: SubscriptionChanged) => void
): () => void {
  function onChanged(payload: SubscriptionChanged) {
    void queryClient.invalidateQueries({ queryKey: BILLING_OVERVIEW_KEY })
    void queryClient.invalidateQueries({ queryKey: ['setup'] })
    if (Date.now() < suppressedUntil || payload.coveredByRep) return
    if (payload.status === 'active') onActivated(payload)
  }

  socket.on('billing:subscription_changed', onChanged)
  return () => {
    socket.off('billing:subscription_changed', onChanged)
  }
}
